import type { MediaInput } from "@/lib/hooks/slides/editor/handlers/types";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateElementProps } from "@/store/slideEditorSlice";
import type {
  DragMatchingImageElement,
  FillBlanksElement,
  OrderImageElement,
} from "@ai-teach/types";

export function useQuestionHandlers() {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);

  const handleQuestionDragAndDrop = (
    { src, id, filename }: MediaInput,
    containerId?: string
  ): boolean => {
    if (!slide || !containerId?.startsWith("question")) return false;

    const [_, questionType, elementId, containerIndex] = containerId.split("//");
    const element = slide.elements.find((el) => el.id === elementId);

    switch (questionType) {
      case "imageChoice": {
        dispatch(
          updateElementProps({
            id: element?.id ?? "",
            props: {
              media: { src, id, filename },
            },
          })
        );
        return true;
      }

      case "dragMatchingImage": {
        const index = Number.parseInt(containerIndex || "0", 10);
        const dragMatchingElement = element as DragMatchingImageElement;

        // Update the image choice at the specified index
        const updatedImageChoices = dragMatchingElement.imageChoices.map((choice, i) =>
          i === index ? { ...choice, media: { src, id, filename } } : choice
        );

        dispatch(
          updateElementProps({
            id: elementId ?? "",
            props: {
              imageChoices: updatedImageChoices,
            },
          })
        );
        return true;
      }

      case "oddImageOut": {
        const index = Number.parseInt(containerIndex || "0", 10);
        const dragMatchingElement = element as DragMatchingImageElement;

        // Update the image choice at the specified index
        const updatedImageChoices = dragMatchingElement.imageChoices.map((choice, i) =>
          i === index ? { ...choice, media: { src, id, filename } } : choice
        );

        dispatch(
          updateElementProps({
            id: elementId ?? "",
            props: {
              imageChoices: updatedImageChoices,
            },
          })
        );
        return true;
      }

      case "dragMatchingText": {
        if (!element || element.type !== "dragMatchingText") return false;

        dispatch(
          updateElementProps({
            id: elementId || "",
            props: {
              media: { src, id, filename },
            },
          })
        );
        return true;
      }

      case "fillBlanksImage": {
        if (!element || element.type !== "fillBlanks") return false;

        const fillBlanksElement = element as FillBlanksElement;
        const index = Number.parseInt(containerIndex || "0", 10);
        const updatedBlocks = fillBlanksElement.blocks.map((block, blockIndex) =>
          blockIndex === index ? { ...block, media: { src, filename, id } } : block
        );

        dispatch(
          updateElementProps({
            id: elementId || "",
            props: {
              blocks: updatedBlocks,
            },
          })
        );
        return true;
      }

      case "orderImage": {
        if (!element || element.type !== "orderImage") return false;

        const index = Number.parseInt(containerIndex || "0", 10);
        const orderImageElement = element as OrderImageElement;

        dispatch(
          updateElementProps({
            id: elementId ?? "",
            props: {
              items: orderImageElement.items.map((item, i) =>
                i === index ? { ...item, media: { src, id, filename } } : item
              ),
            },
          })
        );
        return true;
      }

      case "answerImage": {
        dispatch(
          updateElementProps({
            id: element?.id ?? "",
            props: {
              media: { src, id, filename },
            },
          })
        );
        return true;
      }

      default:
        return false;
    }
  };

  return { handleQuestionDragAndDrop };
}
